import { Injectable, Logger } from '@nestjs/common';
import { Cron } from '@nestjs/schedule';
import { PrismaService } from '@cart/api-data-access';
import { BackupQuotaService } from './backup-quota.service';
import { LocalBackupService } from './local-backup.service';

/**
 * Admission thresholds. Kept here as documented defaults; the service reads
 * overrides from PlatformSetting so they can be tuned from the observed
 * `admissionReason` distribution without a deploy. Expect to move them — these
 * are first guesses, not measurements.
 */
export const SNAPSHOT_RULES = {
  /** Past this many rows, replaying reverse-images is slower than a dump. */
  rowThreshold: 50_000,
  /** Deletes feel unrecoverable, so buy the belt as well as the braces. */
  destructiveDeleteThreshold: 5_000,
  /** A floor regardless of activity. */
  minIntervalHours: 6,
  /** Platform-wide, so an import wave cannot saturate the box. */
  maxConcurrent: 2,
};

export type AdmissionReason =
  | 'row_threshold'
  | 'destructive'
  | 'no_recent_snapshot'
  | 'explicit_request'
  | 'scheduled';

/**
 * L1 merchant snapshots, and the checkpoints that mostly replace them.
 *
 * ── WHY NOT A SNAPSHOT PER BULK OPERATION ────────────────────────────────────
 *
 * An earlier design said "snapshot before every bulk operation". The
 * arithmetic kills it: 10,000 merchants at even 3 bulk operations a day is
 * 30,000 dumps daily, ~11M a year. Each one holds a REPEATABLE READ transaction
 * across ~40 tables — which on a shared cluster holds back vacuum, the part
 * that actually hurts — plus CPU to dump and compress, plus storage against a
 * 1 GB budget, plus Class A operations. It would dominate the entire cost model
 * and turn a background job into a production load source.
 *
 * And it is mostly waste, because `audit.change_log` already holds every
 * before-image. The dump would be a second, far more expensive copy of
 * protection we already have.
 *
 * ── SO: CHECKPOINT BY DEFAULT, SNAPSHOT BY EXCEPTION ─────────────────────────
 *
 * Every bulk operation writes one ~2KB BulkOperationCheckpoint row recording
 * the correlationId. That is the undo handle, and it makes "every bulk
 * operation is undoable" a claim we can actually afford. A full snapshot then
 * happens only when one of the admission rules fires.
 */
@Injectable()
export class MerchantSnapshotService {
  private readonly logger = new Logger(MerchantSnapshotService.name);

  constructor(
    private readonly prisma: PrismaService,
    private readonly quota: BackupQuotaService,
    private readonly local: LocalBackupService
  ) {}

  /**
   * Open a checkpoint before a bulk operation starts.
   *
   * Cheap enough to be unconditional — one INSERT, no transaction held open, no
   * dump. Call this from the CSV importer, the bulk editor, the collection
   * re-sort, and anything else that touches many rows under one correlationId.
   */
  async openCheckpoint(input: {
    organizationId: string;
    storeId?: string;
    correlationId: string;
    operationType: string;
    label?: string;
    actorId?: string;
    actorEmail?: string;
  }): Promise<{ checkpointId: string }> {
    const checkpoint = await this.prisma.bulkOperationCheckpoint.create({
      data: {
        organizationId: input.organizationId,
        storeId: input.storeId,
        correlationId: input.correlationId,
        operationType: input.operationType,
        label: input.label,
        status: 'running',
        actorId: input.actorId,
        actorEmail: input.actorEmail,
        // Must track change_log retention: an undo handle that outlives the
        // before-images it points at is a button that fails when pressed.
        expiresAt: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000),
      },
    });

    return { checkpointId: checkpoint.id };
  }

  /**
   * Close a checkpoint, and decide whether it earned a full snapshot.
   *
   * The decision happens on CLOSE rather than open because the row counts are
   * only known once the operation has run — and those counts are what the
   * thresholds are about.
   */
  async closeCheckpoint(
    checkpointId: string,
    outcome: { affectedTables: string[]; affectedRows: number; deleteCount: number; failed?: boolean }
  ): Promise<{ snapshotId: string | null; admissionReason: AdmissionReason | null; skipped?: string }> {
    const checkpoint = await this.prisma.bulkOperationCheckpoint.update({
      where: { id: checkpointId },
      data: {
        status: outcome.failed ? 'failed' : 'completed',
        affectedTables: outcome.affectedTables,
        affectedRows: outcome.affectedRows,
        deleteCount: outcome.deleteCount,
        isDestructive: outcome.deleteCount > 0,
        finishedAt: new Date(),
      },
    });

    const decision = await this.admit(checkpoint.organizationId, outcome);
    if (!decision.reason) return { snapshotId: null, admissionReason: null };

    const limit = await this.checkLimits(checkpoint.organizationId);
    if (limit) {
      // Recorded rather than silently dropped. The merchant still has the
      // checkpoint, which is the protection that actually mattered — so this is
      // a note, not an incident.
      this.logger.log(`snapshot for ${checkpoint.organizationId} skipped: ${limit}`);
      return { snapshotId: null, admissionReason: decision.reason, skipped: limit };
    }

    const snapshot = await this.create({
      organizationId: checkpoint.organizationId,
      storeId: checkpoint.storeId ?? undefined,
      trigger: 'pre_bulk_operation',
      admissionReason: decision.reason,
      checkpointId: checkpoint.id,
    });

    await this.prisma.bulkOperationCheckpoint.update({
      where: { id: checkpoint.id },
      data: { snapshotId: snapshot.id },
    });

    return { snapshotId: snapshot.id, admissionReason: decision.reason };
  }

  /** Which rule, if any, justifies the expense. */
  private async admit(
    organizationId: string,
    outcome: { affectedRows: number; deleteCount: number }
  ): Promise<{ reason: AdmissionReason | null }> {
    if (outcome.affectedRows > SNAPSHOT_RULES.rowThreshold) {
      return { reason: 'row_threshold' };
    }
    if (outcome.deleteCount > SNAPSHOT_RULES.destructiveDeleteThreshold) {
      return { reason: 'destructive' };
    }

    const since = new Date(Date.now() - 24 * 60 * 60 * 1000);
    const recent = await this.prisma.merchantSnapshot.findFirst({
      where: { organizationId, status: 'completed', snapshotAt: { gte: since } },
    });
    if (!recent) return { reason: 'no_recent_snapshot' };

    return { reason: null };
  }

  /** Rate limits. Returns a human-readable reason when one blocks. */
  private async checkLimits(organizationId: string): Promise<string | null> {
    const since = new Date(Date.now() - SNAPSHOT_RULES.minIntervalHours * 60 * 60 * 1000);
    const recentTriggered = await this.prisma.merchantSnapshot.count({
      where: { organizationId, trigger: 'pre_bulk_operation', snapshotAt: { gte: since } },
    });
    if (recentTriggered > 0) {
      return `one triggered snapshot per ${SNAPSHOT_RULES.minIntervalHours}h per organization`;
    }

    const running = await this.prisma.merchantSnapshot.count({ where: { status: 'running' } });
    if (running >= SNAPSHOT_RULES.maxConcurrent) {
      return `platform snapshot concurrency limit (${SNAPSHOT_RULES.maxConcurrent}) reached`;
    }

    return null;
  }

  /**
   * Take the dump.
   *
   * Consistency comes from a single REPEATABLE READ transaction so the export
   * is one coherent instant across all ~40 tenant tables, and `rowCounts` is
   * recorded for the restore-time cross-check — a dump that restores without
   * anyone checking what came back is a file, not a backup.
   */
  async create(input: {
    organizationId: string;
    storeId?: string;
    trigger: string;
    admissionReason: AdmissionReason;
    checkpointId?: string;
    /** Reuse an already-uploaded dump instead of taking another. */
    sharedDump?: { storageKey: string; sizeBytes: number; checksum: string } | null;
  }): Promise<{ id: string }> {
    const repository = await this.prisma.backupRepository.findFirst({
      where: { kind: 's3', isActive: true },
    });

    if (repository) {
      const verdict = await this.quota.admit(repository.id, 'snapshot');
      if (!verdict.admitted) {
        const refused = await this.prisma.merchantSnapshot.create({
          data: {
            organizationId: input.organizationId,
            storeId: input.storeId,
            trigger: input.trigger,
            admissionReason: input.admissionReason,
            checkpointId: input.checkpointId,
            status: 'refused_quota',
            error: verdict.reason,
          },
        });
        return { id: refused.id };
      }
    }

    const snapshot = await this.prisma.merchantSnapshot.create({
      data: {
        organizationId: input.organizationId,
        storeId: input.storeId,
        trigger: input.trigger,
        admissionReason: input.admissionReason,
        checkpointId: input.checkpointId,
        repositoryId: repository?.id,
        status: 'running',
        schemaVersion: await this.schemaHead(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      },
    });

    const started = Date.now();

    try {
      const rowCounts = await this.countRows(input.organizationId);

      // The whole-database dump is the same operation the logical backup path
      // performs; per-merchant filtering is a later refinement (plan §7.2), and
      // pretending otherwise here would record a merchant-scoped snapshot that
      // is nothing of the kind.
      const dump = input.sharedDump ?? (await this.local.runDump());

      await this.prisma.merchantSnapshot.update({
        where: { id: snapshot.id },
        data: {
          status: 'completed',
          rowCounts: rowCounts as never,
          tableCount: Object.keys(rowCounts).length,
          durationMs: Date.now() - started,
          sizeBytes: BigInt(dump.sizeBytes),
          checksum: dump.checksum,
          storageKey: dump.storageKey,
        },
      });
    } catch (err) {
      await this.prisma.merchantSnapshot.update({
        where: { id: snapshot.id },
        data: { status: 'failed', error: (err as Error).message, durationMs: Date.now() - started },
      });
    }

    return { id: snapshot.id };
  }

  /**
   * The nightly baseline.
   *
   * One per organization per night, which is what the budget supports. Triggered
   * snapshots are the exception on top of this, not a replacement for it.
   */
  /**
   * Take a snapshot per organization.
   *
   * Deliberately NOT a @Cron any more. It had its own schedule while a
   * `snapshot` BackupPolicy also existed — two schedulers for one job, and the
   * policy row was the one showing in the UI. Driven from the policy table now,
   * like every other backup, so "the schedule lives in the table" is true
   * rather than nearly true.
   */
  async runNightly(): Promise<{ created: number; bytes: number; storageKey: string | null }> {
    if (!this.local.isAvailable()) return { created: 0, bytes: 0, storageKey: null };

    let organizations: Array<{ id: string }>;
    try {
      organizations = await this.prisma.organization.findMany({
        where: { isActive: true },
        select: { id: true },
      });
    } catch (err) {
      this.logger.warn(`nightly snapshots skipped: ${(err as Error).message}`);
      return { created: 0, bytes: 0, storageKey: null };
    }

    let created = 0;

    // ── ONE dump, shared ────────────────────────────────────────────────────
    //
    // Until per-merchant filtering exists (plan §7.2), a "merchant snapshot" is
    // a dump of the whole database. Taking one PER ORGANIZATION therefore
    // uploaded N identical copies: five organizations produced 490 MB of
    // uncompressed SQL — 48% of the entire 1 GB budget — to store 98 MB of data
    // five times.
    //
    // One dump, referenced by every row. The rows still record which merchants
    // it covers and what their counts were, which is the part that has value;
    // duplicating the bytes never did.
    let shared: { storageKey: string; sizeBytes: number; checksum: string } | null = null;
    try {
      shared = await this.local.runDump();
    } catch (err) {
      this.logger.warn(`nightly snapshot dump failed: ${(err as Error).message}`);
      return { created: 0, bytes: 0, storageKey: null };
    }

    for (const org of organizations) {
      const running = await this.prisma.merchantSnapshot.count({ where: { status: 'running' } });
      if (running >= SNAPSHOT_RULES.maxConcurrent) {
        // Deliberately give up for tonight rather than queueing: the nightly
        // job has a window, and a queue that outlives it just collides with
        // morning traffic.
        this.logger.warn('nightly snapshots stopped early — concurrency limit reached');
        break;
      }

      try {
        const snap = await this.create({
          organizationId: org.id,
          trigger: 'scheduled',
          admissionReason: 'scheduled',
          // Every organization in this run shares ONE dump.
          sharedDump: shared,
        });
        const row = await this.prisma.merchantSnapshot.findUnique({ where: { id: snap.id } });
        if (row?.status === 'completed') created++;
      } catch (err) {
        this.logger.warn(`snapshot failed for ${org.id}: ${(err as Error).message}`);
      }
    }

    // The bytes are the ONE dump, not the sum of the rows. Summing them
    // reported 490 MB for 98 MB of actual storage — five rows pointing at five
    // identical copies of the whole database.
    // The key travels with the result so the run row can record WHERE the
    // bytes went. Without it the run showed a size and no path, which reads as
    // "nothing was stored" — the opposite of what happened.
    return { created, bytes: shared?.sizeBytes ?? 0, storageKey: shared?.storageKey ?? null };
  }

  /** Per-table counts, for the restore-time cross-check. */
  private async countRows(organizationId: string): Promise<Record<string, number>> {
    const stores = await this.prisma.store.findMany({
      where: { organizationId },
      select: { id: true },
    });
    const storeIds = stores.map((s) => s.id);
    if (!storeIds.length) return {};

    const [products, orders, customers, collections] = await Promise.all([
      this.prisma.product.count({ where: { storeId: { in: storeIds } } }),
      this.prisma.order.count({ where: { storeId: { in: storeIds } } }),
      this.prisma.customer.count({ where: { storeId: { in: storeIds } } }),
      this.prisma.collection.count({ where: { storeId: { in: storeIds } } }),
    ]);

    return { products, orders, customers, collections };
  }

  private async schemaHead(): Promise<string | null> {
    const head = await this.prisma.schemaMigration.findFirst({
      where: { database: 'shopora' },
      orderBy: { sequence: 'desc' },
      select: { filename: true },
    });
    return head?.filename ?? null;
  }

  /**
   * Expire snapshots past their retention.
   *
   * Marks rather than deletes: on a 1 GB budget the object removal is what
   * matters, and that is pgBackRest's expire plus the R2 lifecycle rule. The
   * row is cheap and is the record that the snapshot existed.
   */
  @Cron('40 3 * * *')
  async expire(): Promise<void> {
    const expired = await this.prisma.merchantSnapshot.updateMany({
      where: { status: 'completed', expiresAt: { lt: new Date() } },
      data: { status: 'expired' },
    });
    if (expired.count) this.logger.log(`expired ${expired.count} merchant snapshot(s)`);
  }
}
